import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

let _sharedSocket = null;
let _sharedToken = null;

/** Single shared socket per session — saves handshakes when multiple
 * components subscribe (passenger view + admin tab). */
function getSocket(token) {
  if (_sharedSocket && _sharedToken === token) return _sharedSocket;
  if (_sharedSocket) {
    try { _sharedSocket.disconnect(); } catch (e) { /* */ }
  }
  _sharedToken = token;
  _sharedSocket = io(API_BASE, {
    path: "/api/socket.io",
    transports: ["websocket", "polling"],
    auth: { token },
    reconnection: true,
    reconnectionDelay: 1500,
    reconnectionDelayMax: 8000,
  });
  return _sharedSocket;
}

export default function useChauffeurSocket({ rideId, asDriver = false, modes } = {}) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const handlersRef = useRef(new Map());

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token || token === "pending") return; // not signed in
    const s = getSocket(token);

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    if (s.connected) setConnected(true);

    if (rideId) s.emit("subscribe_ride", { ride_id: rideId });
    if (asDriver) s.emit("driver_online", { modes: modes || ["taxi", "delivery"] });

    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      if (rideId) s.emit("unsubscribe_ride", { ride_id: rideId });
      if (asDriver) s.emit("driver_offline", {});
    };
  }, [rideId, asDriver, JSON.stringify(modes || [])]); // eslint-disable-line

  /** Subscribe to a server-emitted event. Returns an unsubscribe fn. */
  const on = (evt, handler) => {
    const token = localStorage.getItem("auth_token");
    if (!token || token === "pending") return () => {};
    const s = getSocket(token);
    const wrapped = (payload) => { setLastEvent({ evt, payload, ts: Date.now() }); handler(payload); };
    s.on(evt, wrapped);
    handlersRef.current.set(handler, [evt, wrapped]);
    return () => {
      const pair = handlersRef.current.get(handler);
      if (pair) {
        s.off(pair[0], pair[1]);
        handlersRef.current.delete(handler);
      }
    };
  };

  // Auto-cleanup on unmount
  useEffect(() => {
    return () => {
      const token = localStorage.getItem("auth_token");
      if (!token || token === "pending") return;
      const s = getSocket(token);
      handlersRef.current.forEach(([evt, fn]) => s.off(evt, fn));
      handlersRef.current.clear();
    };
  }, []);

  return { connected, on, lastEvent };
}
